export interface BrandColors {
  primary: string;
  accent?: string;
  navy?: string;
}

export const DEFAULT_BRAND_COLORS: BrandColors = {
  primary: '#2563EB',
  accent: '#16A34A',
  navy: '#1E3A8A',
};

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export function isValidHex(value: string): boolean {
  return HEX_RE.test(value);
}

/**
 * Convert a `#RRGGBB` hex string to space-separated HSL channels
 * (`'H S% L%'`, integers, no `hsl()` wrapper, no commas) — matching the
 * shadcn/Tailwind CSS-var format in `index.css` (e.g. `--primary: 221.2 83.2% 53.3%`).
 * Invalid input falls back to the default brand primary so the helper never throws.
 */
export function hexToHslChannels(hex: string): string {
  if (!isValidHex(hex)) {
    // DEFAULT_BRAND_COLORS.primary is always valid, so this recurses at most once.
    return hexToHslChannels(DEFAULT_BRAND_COLORS.primary);
  }

  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) {
      h = (g - b) / d + (g < b ? 6 : 0);
    } else if (max === g) {
      h = (b - r) / d + 2;
    } else {
      h = (r - g) / d + 4;
    }
    h *= 60;
  }

  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Choose a readable foreground (black or white) for a `#RRGGBB` background,
 * returned as HSL channels (`'0 0% 0%'` or `'0 0% 100%'`), using the WCAG
 * relative-luminance black/white crossover (~0.179). Invalid input falls back
 * to the default brand primary.
 */
export function readableForeground(hex: string): string {
  const safeHex = isValidHex(hex) ? hex : DEFAULT_BRAND_COLORS.primary;

  const channels = [
    parseInt(safeHex.slice(1, 3), 16) / 255,
    parseInt(safeHex.slice(3, 5), 16) / 255,
    parseInt(safeHex.slice(5, 7), 16) / 255,
  ].map((c) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));

  const luminance =
    0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];

  return luminance > 0.179 ? '0 0% 0%' : '0 0% 100%';
}
